// src/lib/products.ts
import { supabase } from '../integrations/supabase/client';
import { Product, ProductFromAPI, mapProductFromAPI } from './types'; // Simplified imports

// --- Supabase Functions --- 

interface GetProductsFilters {
  categoryId?: string;
  subcategoryId?: string;
  searchQuery?: string;
  isFeatured?: boolean;
  isNew?: boolean;
  limit?: number;
  offset?: number;
  status?: boolean; // Allow filtering by status explicitly
}

/**
 * Fetches products from Supabase based on filters.
 */
export async function getProducts(filters: GetProductsFilters = {}): Promise<Product[]> {
  let query = supabase
    .from('products')
    .select(`
      *,
      category:categories ( id, name ),
      subcategory:subcategories ( id, name )
    `);
    // .eq('status', true); // Removed default status filter - apply explicitly if needed

  // Apply filters
  if (filters.status !== undefined) { // Apply status filter if provided
      query = query.eq('status', filters.status);
  } else {
      query = query.eq('status', true); // Default to true if not specified
  }
  if (filters.categoryId) {
    query = query.eq('category_id', filters.categoryId);
  }
  if (filters.subcategoryId) {
    query = query.eq('subcategory_id', filters.subcategoryId);
  }
  if (filters.isFeatured) {
    query = query.eq('is_featured', true);
  }
  if (filters.isNew) {
    query = query.eq('is_new', true);
  }
  if (filters.searchQuery) {
    query = query.or(`name.ilike.%${filters.searchQuery}%,description.ilike.%${filters.searchQuery}%`);
  }

  query = query.order('name', { ascending: true });

  if (filters.limit) {
    const from = filters.offset || 0;
    const to = from + filters.limit - 1;
    query = query.range(from, to);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching products:', error);
    return [];
  }

  return (data || []).map(p => mapProductFromAPI(p as ProductFromAPI));
}

/**
 * Fetches a single product by ID from Supabase.
 */
export async function getProductById_Supabase(id: string): Promise<Product | null> {
    const { data, error } = await supabase
        .from('products')
        .select(`
            *,
            category:categories ( id, name ),
            subcategory:subcategories ( id, name ),
            images:product_images ( id, image_url, sort_order ) 
        `)
        .eq('id', id)
        .single();

    if (error) {
        if (error.code === 'PGRST116') { 
            return null;
        }
        console.error('Error fetching product by ID:', error);
        return null;
    }

    if (!data) return null;
    return mapProductFromAPI(data as ProductFromAPI);
}


// --- Placeholder CRUD Functions --- 
// Needs proper form data types

export interface ProductFormData {
    name: string;
    description: string;
    full_description?: string | null;
    price: number;
    stock: number;
    status?: boolean; // default true
    is_featured?: boolean; // default false
    is_new?: boolean; // default false
    discount?: number | null;
    image_url?: string | null; // Main image
    category_id?: string | null;
    subcategory_id?: string | null;
    // add variants, additional images if managed through this form
}

export async function createProduct(formData: ProductFormData): Promise<{ data: Product | null, error: string | null }> {
    const dataToInsert = {
        ...formData,
        status: formData.status ?? true,
        is_featured: formData.is_featured ?? false,
        is_new: formData.is_new ?? false,
    };

    const { data, error } = await supabase
        .from('products')
        .insert(dataToInsert)
        .select(`*, category:categories(id, name), subcategory:subcategories(id, name)`) 
        .single();

    if (error) {
        console.error('Error creating product:', error);
        return { data: null, error: error.message };
    }

    return { data: data ? mapProductFromAPI(data as ProductFromAPI) : null, error: null };
}

export async function updateProduct(id: string, formData: Partial<ProductFormData>): Promise<{ data: Product | null, error: string | null }> {
     const dataToUpdate = {
       ...formData,
       updated_at: new Date() 
   };
    
    if (formData.subcategory_id === '') {
        dataToUpdate.subcategory_id = null;
    }
    if (formData.category_id === '') {
        dataToUpdate.category_id = null;
    }

    const { data, error } = await supabase
        .from('products')
        .update(dataToUpdate)
        .eq('id', id)
        .select(`*, category:categories(id, name), subcategory:subcategories(id, name)`) 
        .single();

    if (error) {
        console.error('Error updating product:', error);
        return { data: null, error: error.message };
    }
    return { data: data ? mapProductFromAPI(data as ProductFromAPI) : null, error: null };
}


// --- Removed DEPRECATED Hardcoded Data and Functions --- 

console.info("src/lib/products.ts: Deprecated hardcoded data and functions have been removed.");

// No more hardcoded categories_hardcoded, categoryMap_hardcoded, products_hardcoded

// No more deprecated functions: 
// getProductsByCategory, getProductById, getFeaturedProducts, getNewProducts, 
// getDiscountedProducts, getRelatedProducts, getCategoryName, getCategoryById, 
// getAllCategories, searchProducts

